import { Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { IMethod, MethodType } from '../../models/structure/components/basic';
import { DataProvider } from '../../providers/basic/data.service';

@Injectable({
  providedIn: 'root'
})
export class MethodsService extends DataProvider<any> {
  /**
   * Calls the specified method
   * @param method to call
   * @param urlParams to append at the back with ?
   * @param data to use for the method (will not get mapped to CamelCase!)
   * @param params to use for replacing the {x} variables inside the method url
   * @param options `skipEncoding` leaves the url params unencoded, `headers` adds request headers
   * on top of the ones the method itself carries (e.g. `viewRender` to mark a call as a real view).
   * @returns the result of the method or null if no method is specified
   */
  callMethod<T>(
    method: IMethod | undefined,
    urlParams?: Record<string, any> | null,
    data?: Record<string, any> | null,
    params?: Record<string, any> | null,
    options?: { skipEncoding?: boolean; headers?: Record<string, any> }
  ): Observable<T | undefined> {
    if (!method) return of(undefined);

    let methodUrl = this.normalizeUrl(this.buildUrlWithParameters(method, params));
    const parts = methodUrl.split('/');
    parts.shift();
    const url = this.url + parts.join('/');

    return this.generateRequest<T>(
      method.method,
      url,
      this.getUrlParams(method, urlParams),
      data || {},
      { ...method.headers, ...options?.headers },
      undefined,
      options?.skipEncoding
    );
  }

  /**
   * Calls the specified method and expects a Blob response
   * @param method to call
   * @param urlParams to append at the back with ?
   * @param params to use for replacing the {x} variables inside the method url
   * @returns the result of the method or null if no method is specified
   */

  callBlobMethod<Blob>(
    method: IMethod | undefined,
    urlParams?: Record<string, any> | null,
    params?: Record<string, any> | null,
    options?: { skipEncoding: boolean }
  ): Observable<Blob | undefined> {
    if (!method) return of(undefined);

    let methodUrl = this.normalizeUrl(this.buildUrlWithParameters(method, params));
    const parts = methodUrl.split('/');
    parts.shift();
    const url = this.url + parts.join('/');

    return this.generateRequest<Blob>(
      method.method,
      url,
      this.getUrlParams(method, urlParams),
      undefined,
      method.headers ?? [],
      'blob',
      options?.skipEncoding
    );
  }

  /**
   * Removes trailing "?" or "&" characters from a URL.
   * This ensures URLs are clean before query parameters are appended.
   *
   * @param url - The URL string to normalize.
   * @returns The normalized URL string.
   */
  private normalizeUrl(url: string): string {
    return url.replace(/[?&]+$/, '');
  }

  /**
   * Constructs a url from the given parameters and url
   * @param method to use for constructing the correct url
   * @returns the correct url to call
   */
  private buildUrlWithParameters(method: IMethod, params: any): string {
    let urlWithParameters = method.url;

    const regexCurlyParams = /{([^}]+)}/g; // Match anything inside curly braces
    const urlAttributeMatches = method.url.matchAll(regexCurlyParams);
    const urlAttributes = Array.from(urlAttributeMatches, (match) => match[1]);

    if (method.parameters && method.parameters.length > 0 && urlAttributes.length > 0) {
      urlAttributes.forEach((attr) => {
        // Check if the param exists in the passed params object
        if (!(attr in params)) {
          throw new Error(`Required parameter not found: "${attr}"`);
        }

        // Replace the placeholder {paramName} in the URL with the actual value from params
        const paramValue = encodeURIComponent(params[attr]);
        urlWithParameters = urlWithParameters.replace(`{${attr}}`, paramValue);
      });
    }
    return urlWithParameters;
  }

  /**
   * Returns the parameters for the method as an object
   * @param method to get the parameters settings from
   * @param data to use for the parameters if specified
   * @returns the parameters for the method as an object
   */
  getUrlParams(method: IMethod | undefined, data?: any): Record<string, any> {
    if (!method || !data) return {};
    const params: Record<string, any> = {};
    method.parameters.forEach((param) => {
      const isValidNumber = typeof data[param.name] === 'number' && !isNaN(data[param.name]);
      const isNotUnset = data[param.name] !== undefined && data[param.name] !== null;
      if (isValidNumber || isNotUnset) params[param.name] = data[param.name];
    });
    return params;
  }

  /**
   * Makes an HTTP request based on the specified method type.
   *
   * @template T - The expected response type.
   * @param {MethodType} method - The HTTP method to use (e.g., 'get', 'post', 'put', 'delete').
   * @param {string} url - The URL to send the request to.
   * @param {any} [args={}] - Optional query parameters to include in the request.
   * @param {any} [payload={}] - Optional payload to include in the request body (for 'post' and 'put' methods).
   * @param {any} [headers={}] - Optional headers to include in the request.
   * @returns {Observable<T>} - An observable that emits the response data.
   */
  generateRequest<T>(
    method: MethodType,
    url: string,
    args = {},
    payload = {},
    headers = {},
    responseType?: 'blob' | 'arraybuffer' | 'text',
    skipEncoding?: boolean
  ): Observable<T> {
    const encodedArgs = skipEncoding ? args : this.encodeQueryParams(args);
    if (responseType) {
      return this.http
        .request(method, this.generateUrl(url, encodedArgs), {
          responseType: responseType,
          headers: this.generateHeaders(headers, false)
        })
        .pipe(catchError((err) => this.errorService.convert(err)));
    }
    switch (method) {
      case MethodType.GET:
        return this.http
          .get<T>(this.generateUrl(url, encodedArgs), { headers: this.generateHeaders(headers, false) })
          .pipe(
            map((data) => {
              return DataProvider.mapCasesReverse(data);
            })
          )
          .pipe(catchError((err) => this.errorService.convert(err)));
      case MethodType.POST:
        return this.http
          .post<T>(this.generateUrl(url, encodedArgs), this.formatPayload(payload, true), { headers: this.generateHeaders(headers, true) })
          .pipe(
            map((data) => {
              return DataProvider.mapCasesReverse(data);
            })
          )
          .pipe(catchError((err) => this.errorService.convert(err)));
      case MethodType.PUT:
        return this.http
          .put<T>(this.generateUrl(url, encodedArgs), this.formatPayload(payload, true), { headers: this.generateHeaders(headers, true) })
          .pipe(
            map((data) => {
              return DataProvider.mapCasesReverse(data);
            })
          )
          .pipe(catchError((err) => this.errorService.convert(err)));
      case MethodType.DELETE:
        return this.http
          .delete<T>(this.generateUrl(url, encodedArgs), { headers: this.generateHeaders(headers, false) })
          .pipe(
            map((data) => {
              return DataProvider.mapCasesReverse(data);
            })
          )
          .pipe(catchError((err) => this.errorService.convert(err)));
      default:
        throw new Error(`Method not supported: ${method}`);
    }
  }

  /**
   * Generates a URL with query parameters based on the provided URL and arguments.
   * @param url - The base URL to which query parameters will be added.
   * @param args - An object containing query parameters to be added to the URL.
   * @returns A string representing the generated URL with query parameters.
   * If no arguments are provided, the original URL is returned.
   */
  generateUrl(url: string, args: any): string {
    if (!args) return url;
    const separator = url.includes('?') ? '&' : '?';
    const urlWithArgs = new URL(url + separator + this.paramsToURI(args)).toString();
    return urlWithArgs;
  }

  /**
   * Encodes specific query parameters in the provided arguments object.
   *
   * This method creates a copy of the input object to avoid modifying the original.
   * It then iterates over the keys of the copied object and encodes the value of any key
   * that matches 'query' (case-insensitive) using Base64 encoding.
   *
   * @param args - An object containing query parameters to be encoded. Defaults to an empty object.
   * @returns A new object with the 'query' parameter encoded in Base64, if present.
   */
  private encodeQueryParams(args: any = {}): any {
    if (!args) return args;

    const encodedArgs = { ...args };
    Object.keys(encodedArgs).forEach((key) => {
      if (key.toLowerCase() === 'query' || key.toLowerCase() === 'search') {
        encodedArgs[key] = btoa(encodedArgs[key]);
      }
    });
    return encodedArgs;
  }
}
